/**
 * Login page — the only unauthenticated route in the app.
 *
 * Visual language is the Phase-3 design tokens applied: warm-neutral surface
 * card, restrained typography, single accent on the primary button. No
 * "AI gradient" stuff.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Eye, EyeOff, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../lib/api-client';
import { cn } from '../lib/cn';

const LoginFormSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof LoginFormSchema>;

// After signing in we always land on the Chats view — the universal home for
// every role. Admins reach the console from the "Admin" top tab. We do NOT
// restore the pre-login page (a stale module page like /media-calendar felt
// like a bug); the destination is intentionally fixed.
const POST_LOGIN_DEST = '/chat';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { user, status, login, verifyMfa } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mfaState, setMfaState] = useState<{ challengeToken: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: { email: '', password: '' },
  });

  // If already authenticated, bounce away from /login.
  useEffect(() => {
    if (status === 'authenticated' && user) {
      navigate(POST_LOGIN_DEST, { replace: true });
    }
  }, [status, user, navigate]);

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.kind === 'mfa_required') {
        setMfaState({ challengeToken: result.challengeToken });
        setMfaError(null);
        setSubmitting(false);
        return;
      }
      const loggedIn = result.user;
      toast.success(`Welcome back, ${loggedIn.displayName}`);
      navigate(POST_LOGIN_DEST, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'INVALID_CREDENTIALS') {
          setError('password', { type: 'manual', message: 'Email or password is incorrect' });
        } else if (err.code === 'ACCOUNT_LOCKED') {
          setError('email', { type: 'manual', message: err.message });
        } else if (err.code === 'RATE_LIMITED') {
          setError('email', { type: 'manual', message: 'Too many attempts — slow down.' });
        } else {
          setError('email', { type: 'manual', message: err.message });
        }
      } else {
        setError('email', { type: 'manual', message: 'Something went wrong' });
      }
    } finally {
      setSubmitting(false);
    }
  });

  const onMfaSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!mfaState || !mfaCode.trim()) return;
    setSubmitting(true);
    setMfaError(null);
    try {
      const loggedIn = await verifyMfa(mfaState.challengeToken, mfaCode.trim());
      toast.success(`Welcome back, ${loggedIn.displayName}`);
      navigate(POST_LOGIN_DEST, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'MFA_CHALLENGE_EXPIRED') {
          setMfaState(null);
          setMfaCode('');
          setMfaError('Code expired — please sign in again.');
        } else {
          setMfaError(err.message || 'Invalid code');
        }
      } else {
        setMfaError('Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-full place-items-center px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <img
            src="/peerless-logo.png"
            alt="Peerless"
            className="mb-4 h-9 w-auto select-none invert dark:invert-0"
            draggable={false}
          />
          <p className="font-mono text-xs uppercase tracking-tight text-ink-muted">AI Portal</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-ink">
            {mfaState ? 'Verify it’s you' : 'Sign in'}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {mfaState
              ? 'Open your authenticator app and enter the 6-digit code, or use a backup code.'
              : 'Use the credentials your administrator provided.'}
          </p>
        </div>

        {mfaState && (
          <form onSubmit={onMfaSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="mfa-code" className="block text-sm font-medium text-ink">
                Authentication code
              </label>
              <input
                id="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                spellCheck={false}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className={cn(
                  'mt-1.5 block w-full rounded-tight border bg-paper px-3 py-2 text-sm tracking-widest text-ink outline-none transition',
                  'focus:border-accent',
                  mfaError ? 'border-danger' : 'border-rule',
                )}
              />
              {mfaError && (
                <p className="mt-1 flex items-center gap-1 text-xs text-danger">
                  <AlertCircle className="size-3" />
                  {mfaError}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting || !mfaCode.trim()}
              className={cn(
                'inline-flex w-full items-center justify-center gap-2 rounded-tight bg-accent px-4 py-2.5 text-sm font-medium text-accent-fg transition',
                'hover:opacity-90 disabled:opacity-50',
              )}
            >
              {submitting ? 'Verifying…' : 'Verify and continue'}
            </button>
            <button
              type="button"
              onClick={() => { setMfaState(null); setMfaCode(''); setMfaError(null); }}
              className="block w-full text-center text-xs text-ink-muted hover:text-ink"
            >
              ← Back to sign in
            </button>
          </form>
        )}

        {!mfaState && (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              spellCheck={false}
              {...register('email')}
              className={cn(
                'mt-1.5 block w-full rounded-tight border bg-paper px-3 py-2 text-sm text-ink outline-none transition',
                'focus:border-accent',
                errors.email ? 'border-danger' : 'border-rule',
              )}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <p id="email-error" className="mt-1 flex items-center gap-1 text-xs text-danger">
                <AlertCircle className="size-3" />
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ink">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                {...register('password')}
                className={cn(
                  'mt-1.5 block w-full rounded-tight border bg-paper px-3 py-2 pr-10 text-sm text-ink outline-none transition',
                  'focus:border-accent',
                  errors.password ? 'border-danger' : 'border-rule',
                )}
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? 'password-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-tight p-1.5 text-ink-muted hover:bg-paper-soft hover:text-ink"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            {errors.password && (
              <p id="password-error" className="mt-1 flex items-center gap-1 text-xs text-danger">
                <AlertCircle className="size-3" />
                {errors.password.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={cn(
              'inline-flex w-full items-center justify-center gap-2 rounded-tight bg-accent px-4 py-2.5 text-sm font-medium text-accent-fg transition',
              'hover:opacity-90 disabled:opacity-50',
            )}
          >
            {submitting ? (
              <>
                <span className="size-3 animate-pulse rounded-tight bg-accent-fg" />
                Signing in…
              </>
            ) : (
              <>
                <LogIn className="size-4" />
                Sign in
              </>
            )}
          </button>
        </form>
        )}
      </div>
    </main>
  );
}
